import React from 'react';
import { Box, Button, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { CONTROL, PRES, SPACE, TYPE } from '../chrome';
import { COPY } from '../presentationCopy';
import { PresentationSettingsState, SettingsColor, SettingsColorGroup } from '../presentationSettings';
import { focusVisibleSx } from '../ui/focus';
import { slugColorId } from './adminSettingsState';
import AdminColorSelect from './AdminColorSelect';
import { AdminLabeledControl } from './AdminInfoLabel';
import AdminSettingCard from './AdminSettingCard';
import AdminVisibilitySwitch from './AdminVisibilitySwitch';
import { adminFieldSx } from './adminFormSx';

interface AdminColorsSectionProps {
  draft: PresentationSettingsState;
  setDraft: React.Dispatch<React.SetStateAction<PresentationSettingsState>>;
}

function hexFor(draft: PresentationSettingsState, id: string): string {
  return draft.colors.find((color) => color.id === id)?.hex || '#1B3967';
}

const AdminColorsSection: React.FC<AdminColorsSectionProps> = ({ draft, setDraft }) => {
  const visibleCount = draft.colors.filter((color) => !color.hidden).length;

  const updateColor = (index: number, patch: Partial<SettingsColor>) => {
    setDraft((current) => ({
      ...current,
      colors: current.colors.map((color, i) => (i === index ? { ...color, ...patch } : color)),
    }));
  };

  const moveColor = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= draft.colors.length) return;
    setDraft((current) => {
      const colors = [...current.colors];
      const [row] = colors.splice(index, 1);
      colors.splice(next, 0, row);
      return { ...current, colors };
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${SPACE[5]}px` }}>
      <AdminSettingCard title={COPY.settingsDefaultsCard} description={COPY.settingsColorsConsequence}>
        <AdminVisibilitySwitch
          checked={draft.allowCustomHex}
          label={COPY.settingsAllowCustomHex}
          onChange={(checked) => setDraft((current) => ({ ...current, allowCustomHex: checked }))}
        />
        <Typography sx={{ mt: 0.5, mb: 2, color: PRES.textTertiary, fontFamily: TYPE.ui, ...TYPE.caption }}>
          {COPY.settingsAllowCustomHexHelp}
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          mb: 2,
        }}
        >
          <AdminLabeledControl label={COPY.settingsDefaultTextColor} hint={COPY.settingsDefaultTextColorHelp}>
            {({ id, describedBy }) => (
              <AdminColorSelect
                id={id}
                describedBy={describedBy}
                value={draft.defaultTextColorId}
                colors={draft.colors}
                onChange={(value) => setDraft((current) => ({ ...current, defaultTextColorId: value }))}
                fullWidth
              />
            )}
          </AdminLabeledControl>
          <AdminLabeledControl label={COPY.settingsDefaultShapeColor} hint={COPY.settingsDefaultShapeColorHelp}>
            {({ id, describedBy }) => (
              <AdminColorSelect
                id={id}
                describedBy={describedBy}
                value={draft.defaultShapeColorId}
                colors={draft.colors}
                onChange={(value) => setDraft((current) => ({ ...current, defaultShapeColorId: value }))}
                fullWidth
              />
            )}
          </AdminLabeledControl>
        </Box>
        <Typography sx={{ mb: 1, color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.label }}>
          {COPY.settingsColorPreview}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box
            role="img"
            aria-label={COPY.settingsColorPreviewText}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: `${CONTROL.radius}px`,
              border: `1px solid ${PRES.borderSubtle}`,
              color: hexFor(draft, draft.defaultTextColorId),
              fontFamily: TYPE.ui,
              ...TYPE.body,
              fontWeight: 650,
            }}
          >
            {COPY.settingsColorPreviewSample}
          </Box>
          <Box sx={{
            width: 72,
            height: 36,
            borderRadius: `${CONTROL.radius}px`,
            bgcolor: hexFor(draft, draft.defaultShapeColorId),
            border: `1px solid ${PRES.borderStrong}`,
          }}
            role="img"
            aria-label={COPY.settingsColorPreviewShape}
          />
        </Box>
      </AdminSettingCard>

      <AdminSettingCard title={COPY.settingsPaletteCard}>
        <Box sx={{
          display: { xs: 'none', md: 'grid' },
          gridTemplateColumns: '40px minmax(140px, 1.4fr) 140px 150px 150px 88px',
          gap: 1,
          px: 0.5,
          mb: 1,
        }}
        >
          {[COPY.settingsColorPreview, COPY.settingsColorName, COPY.settingsColorHex, COPY.settingsColorGroup, COPY.settingsVisible, ''].map((heading) => (
            <Typography key={heading || 'order'} sx={{ color: PRES.textTertiary, fontFamily: TYPE.ui, ...TYPE.caption }}>
              {heading}
            </Typography>
          ))}
        </Box>
        <Typography sx={{ mb: 1.5, color: PRES.textTertiary, fontFamily: TYPE.ui, ...TYPE.caption }}>
          {COPY.settingsColorHexHelp}
        </Typography>
        {draft.colors.map((color, index) => {
          const lastVisible = !color.hidden && visibleCount <= 1;
          return (
            <Box
              key={color.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '40px 1fr', md: '40px minmax(140px, 1.4fr) 140px 150px 150px 88px' },
                gap: 1,
                alignItems: 'center',
                mb: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: color.hex,
                  border: `1px solid ${PRES.borderStrong}`,
                }}
              />
              <TextField
                size="small"
                fullWidth
                value={color.name}
                onChange={(event) => updateColor(index, { name: event.target.value })}
                inputProps={{ 'aria-label': COPY.settingsColorName }}
                sx={adminFieldSx()}
              />
              <TextField
                size="small"
                fullWidth
                value={color.hex}
                onChange={(event) => updateColor(index, { hex: event.target.value.toUpperCase() })}
                inputProps={{ 'aria-label': COPY.settingsColorHex, spellCheck: false }}
                sx={adminFieldSx()}
              />
              <Select
                size="small"
                fullWidth
                value={color.group}
                onChange={(event) => updateColor(index, { group: event.target.value as SettingsColorGroup })}
                inputProps={{ 'aria-label': COPY.settingsColorGroup }}
                sx={adminFieldSx()}
              >
                <MenuItem value="primary">{COPY.settingsColorGroupPrimary}</MenuItem>
                <MenuItem value="complement">{COPY.settingsColorGroupComplement}</MenuItem>
                <MenuItem value="accent">{COPY.settingsColorGroupAccent}</MenuItem>
              </Select>
              <AdminVisibilitySwitch
                checked={!color.hidden}
                disabled={lastVisible}
                disabledReason={lastVisible ? COPY.settingsLastVisibleColor : undefined}
                label={COPY.settingsVisible}
                onChange={(checked) => updateColor(index, { hidden: !checked })}
              />
              <Box>
                <IconButton
                  size="small"
                  aria-label={COPY.settingsMoveUp}
                  onClick={() => moveColor(index, -1)}
                  disabled={index === 0}
                  sx={focusVisibleSx}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={COPY.settingsMoveDown}
                  onClick={() => moveColor(index, 1)}
                  disabled={index === draft.colors.length - 1}
                  sx={focusVisibleSx}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        })}
        <Button
          onClick={() => setDraft((current) => {
            const id = slugColorId('Ny färg', current.colors.map((item) => item.id));
            return {
              ...current,
              colors: [...current.colors, { id, hex: '#1B3967', name: 'Ny färg', group: 'primary', hidden: false }],
            };
          })}
          sx={{ mt: 1, textTransform: 'none', ...focusVisibleSx }}
        >
          {COPY.settingsAddColor}
        </Button>
      </AdminSettingCard>
    </Box>
  );
};

export default AdminColorsSection;
